import "./App.css";
import "leaflet/dist/leaflet.css";
import "@/lib/DayJs/DayJs";
import "@/lib/polyfills/withResolvers";
import "@/lib/polyfills/urlParse";
import { QueryClientProvider } from "@tanstack/react-query";
import { use, useEffect, Suspense } from "react";
import { QueryClient } from "./lib/ReactQuery/QueryClient";
import { RouterProvider } from "@tanstack/react-router";
import { router } from "@/routes";
import { GenericErrorBoundary } from "@/lib/Components/Layout/ErrorBoundary/ErrorBoundary";
import { TooltipProvider } from "@/components/ui/tooltip";
import * as Sentry from "@sentry/react";
import { IntlProvider } from "use-intl";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useLocaleStore, type Locale } from "@/lib/i18n/useLocale";
import enMessages from "./messages/generated/en.json";
import { useAuth } from "@/lib/Auth/useAuth";
import { AuthProvider } from "@/lib/Auth/AuthProvider";

// Register your router for maximum type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

if (!Sentry.getClient()) {
  Sentry.init({
    enabled: import.meta.env.PROD,
    environment: import.meta.env.PROD ? "production" : "development",
    dsn: "https://81119c0b337afb0270a2b214fd3e46dd@o4506212263067648.ingest.us.sentry.io/4508607745818624",
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
      }),
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.browserTracingIntegration(),
      Sentry.feedbackIntegration({
        colorScheme: "system",
        showBranding: false,
        autoInject: false,
        useSentryUser: {
          email: "email",
          name: "username",
        },
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      // Drop errors that occur during page unload (expected aborted requests)
      if (window.__isUnloading) {
        return null;
      }
      return event;
    },
  });
}

// en is statically imported above (eager base locale); exclude it here so it
// isn't pulled into the dynamic glob, which would emit an ineffective-dynamic
// -import warning and keep en out of its own lazy chunk.
const localeLoaders = import.meta.glob<{ default: Record<string, string> }>([
  "./messages/generated/*.json",
  "!./messages/generated/en.json",
]);

async function loadMessages(locale: string) {
  if (locale === "en") {
    return enMessages;
  }
  const loader = localeLoaders[`./messages/generated/${locale}.json`];
  if (!loader) {
    return enMessages;
  }
  try {
    const messages = await loader();
    return messages.default;
  } catch {
    return enMessages;
  }
}

type Localization = { messages: Record<string, any> };
type FulfilledThenable<T> = Promise<T> & { status: "fulfilled"; value: T };

// Survives a hot update of this module so React keeps seeing the same
// promises and the Suspense boundary above the app never falls back.
const localizationCache: Map<Locale, Promise<Localization>> = import.meta.hot
  ?.data.localizationCache ?? new Map();
if (import.meta.hot) {
  import.meta.hot.data.localizationCache = localizationCache;
}

function fulfilled<T>(value: T): FulfilledThenable<T> {
  return Object.assign(Promise.resolve(value), {
    status: "fulfilled" as const,
    value,
  });
}

function getLocalization(locale: Locale): Promise<Localization> {
  if (locale === "en") {
    return fulfilled({ messages: enMessages });
  }

  if (!localizationCache.has(locale)) {
    localizationCache.set(
      locale,
      loadMessages(locale).then((messages) => ({
        messages,
      })),
    );
  }
  return localizationCache.get(locale)!;
}

function AppErrorFallback({
  error,
  resetError,
}: {
  error: unknown;
  resetError: () => void;
}) {
  const message =
    error instanceof Error ? error.message : "An unknown error occurred";

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="max-w-md space-y-4 p-6 text-center">
        <h1 className="text-lg font-semibold text-gray-900">
          Failed to load application
        </h1>
        <div className="rounded-md bg-red-50 p-3 text-left text-sm text-red-700">
          {message}
        </div>
        <button
          onClick={resetError}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

export default function AppWrapper() {
  const locale = useLocaleStore((state) => state.locale);

  return (
    <Sentry.ErrorBoundary fallback={AppErrorFallback}>
      <Suspense fallback={null}>
        <AppContent locale={locale} />
      </Suspense>
    </Sentry.ErrorBoundary>
  );
}

function AppContent({ locale }: { locale: Locale }) {
  const { messages } = use(getLocalization(locale));

  return (
    <IntlProvider messages={messages} locale={locale}>
      <QueryClientProvider client={QueryClient}>
        <AuthProvider>
          <GenericErrorBoundary>
            <TooltipProvider>
              <InnerApp />
            </TooltipProvider>
          </GenericErrorBoundary>
        </AuthProvider>
        <QueryClientPlugin />
      </QueryClientProvider>
    </IntlProvider>
  );
}

function InnerApp() {
  const auth = useAuth();

  // TanStack Router does not re-run route beforeLoad guards when the
  // context changes. After auth resolves (e.g. post-login), re-validate
  // so guards using context.auth pick up the new state.
  useEffect(() => {
    router.invalidate();
  }, [auth.isSignedIn, auth.organisations.length]);

  return <RouterProvider router={router} context={{ auth }} />;
}

function QueryClientPlugin() {
  return <ReactQueryDevtools buttonPosition="bottom-right" />;
}
